import { decodeHtmlEntities } from '@/util/stripHtml';

export interface SurveyTextRun {
  text: string;
  bold?: boolean;
  italic?: boolean;
}

export type SurveyHtmlBlock =
  | { type: 'paragraph'; runs: SurveyTextRun[] }
  | { type: 'bullet-list'; items: SurveyTextRun[][] };

const BLOCK_PATTERN = /<ul[^>]*>[\s\S]*?<\/ul>|<p[^>]*>[\s\S]*?<\/p>/gi;

const LIST_ITEM_PATTERN = /<li[^>]*>([\s\S]*?)<\/li>/gi;

const INLINE_TAG_PATTERN = /<(\/?)(strong|b|em|i|br)\s*\/?>/gi;

function stripUnknownTags(text: string): string {
  return text.replace(/<[^>]+>/g, '');
}

/**
 * Converts an inline HTML fragment into styled text runs, keeping only
 * `<strong>`/`<b>`, `<em>`/`<i>`, and `<br>`. Every other tag is stripped but
 * its text content is kept — this never feeds back into an HTML renderer, so
 * an unsupported or malformed tag degrades to plain text rather than breaking
 * the survey.
 */
function parseInlineRuns(fragment: string): SurveyTextRun[] {
  const runs: SurveyTextRun[] = [];

  let boldDepth = 0;

  let italicDepth = 0;

  let lastIndex = 0;

  let match: RegExpExecArray | null;

  INLINE_TAG_PATTERN.lastIndex = 0;

  while ((match = INLINE_TAG_PATTERN.exec(fragment)) !== null) {
    const [, closing, tagName] = match;

    const textBefore = decodeHtmlEntities(stripUnknownTags(fragment.slice(lastIndex, match.index)));

    if (textBefore) {
      runs.push({ text: textBefore, bold: boldDepth > 0, italic: italicDepth > 0 });
    }

    const tag = tagName.toLowerCase();

    if (tag === 'br') {
      runs.push({ text: '\n', bold: boldDepth > 0, italic: italicDepth > 0 });
    } else if (tag === 'strong' || tag === 'b') {
      boldDepth = Math.max(0, boldDepth + (closing ? -1 : 1));
    } else if (tag === 'em' || tag === 'i') {
      italicDepth = Math.max(0, italicDepth + (closing ? -1 : 1));
    }

    lastIndex = INLINE_TAG_PATTERN.lastIndex;
  }

  const remaining = decodeHtmlEntities(stripUnknownTags(fragment.slice(lastIndex)));

  if (remaining) {
    runs.push({ text: remaining, bold: boldDepth > 0, italic: italicDepth > 0 });
  }

  return runs;
}

/**
 * Parses a survey `html` element's markup into a small block structure
 * (`paragraph` / `bullet-list`) for `SurveyHtmlBlock` to render with our own
 * `Text`/`VStack` primitives — deliberately not a general HTML renderer.
 * Only paragraphs, bullet lists, bold, and italic are recognized; every
 * other tag is stripped to plain text rather than rendered.
 */
export function parseSurveyHtml(html: string): SurveyHtmlBlock[] {
  const blocks: SurveyHtmlBlock[] = [];

  let lastIndex = 0;

  let match: RegExpExecArray | null;

  BLOCK_PATTERN.lastIndex = 0;

  const pushParagraph = (fragment: string) => {
    const trimmed = fragment.trim();

    if (!trimmed) {
      return;
    }

    const runs = parseInlineRuns(trimmed);

    if (runs.length > 0) {
      blocks.push({ type: 'paragraph', runs });
    }
  };

  while ((match = BLOCK_PATTERN.exec(html)) !== null) {
    pushParagraph(html.slice(lastIndex, match.index));

    const matched = match[0];

    if (/^<ul/i.test(matched)) {
      const items: SurveyTextRun[][] = [];

      let itemMatch: RegExpExecArray | null;

      LIST_ITEM_PATTERN.lastIndex = 0;

      while ((itemMatch = LIST_ITEM_PATTERN.exec(matched)) !== null) {
        items.push(parseInlineRuns(itemMatch[1]));
      }

      if (items.length > 0) {
        blocks.push({ type: 'bullet-list', items });
      }
    } else {
      pushParagraph(matched.replace(/^<p[^>]*>/i, '').replace(/<\/p>$/i, ''));
    }

    lastIndex = BLOCK_PATTERN.lastIndex;
  }

  pushParagraph(html.slice(lastIndex));

  return blocks;
}
